import { Button, CloseButton } from '@consigli/facade';
import { Country, type ProcessorPreferenceKey, ServiceName } from '@consigli/types';
import { type FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaAngleDown, FaAngleRight } from 'react-icons/fa';
import { HiOutlineDocumentText } from 'react-icons/hi2';

import {
  getAnalysisPreviewSections,
  getServiceLabelKey,
  type AnalysisPreviewSection,
} from './analysis-preview-steps';

export interface AnalysisPreviewModalProps {
  serviceName: ServiceName;
  documentsCount: number;
  hasChecklist: boolean;
  needsClassification?: boolean;
  country?: Country | null;
  selectedConflictTypes?: ProcessorPreferenceKey[];
  isLoading?: boolean;
  onClose: () => void;
}

const SectionRow: FC<{
  section: AnalysisPreviewSection;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ section, isOpen, onToggle }) => {
  const { t } = useTranslation();
  const Icon = section.icon;

  return (
    <div className="rounded-lg border border-day-light-2">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`analysis-preview-section-${section.id}`}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <Icon
            size={20}
            className="shrink-0 text-day-neutral-subtle"
            aria-hidden
            data-testid={`analysis-preview-section-icon-${section.id}`}
          />
          <span className="text-base font-semibold text-day-neutral-dark">
            {t(section.titleKey)}
          </span>
        </div>
        {isOpen ? (
          <FaAngleDown size={14} className="text-day-neutral-subtle" />
        ) : (
          <FaAngleRight size={14} className="text-day-neutral-subtle" />
        )}
      </button>
      {isOpen && (
        <ul
          id={`analysis-preview-section-${section.id}`}
          className="list-disc list-outside marker:text-day-neutral-subtle space-y-3 pl-10 pr-4 pb-4 pt-1"
        >
          {section.steps.map((step) => (
            <li key={step.id} data-testid={`analysis-preview-step-${step.id}`} className="pl-1">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-semibold text-day-neutral-dark">
                  {t(step.nameKey)}
                </span>
                <span className="text-xs text-day-neutral-subtle">{t(step.descriptionKey)}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const AnalysisPreviewModal: FC<AnalysisPreviewModalProps> = ({
  serviceName,
  documentsCount,
  hasChecklist,
  needsClassification,
  country,
  selectedConflictTypes,
  isLoading = false,
  onClose,
}) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);

  const sections = getAnalysisPreviewSections(serviceName, {
    hasChecklist,
    needsClassification,
    country,
    selectedConflictTypes,
  });
  const firstSectionId = sections[0]?.id ?? null;
  const [openSectionId, setOpenSectionId] = useState<string | null>(firstSectionId);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key !== 'Tab' || !dialogRef.current) return;

      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute('disabled'));

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (sections.length === 0) return null;

  const serviceLabelKey = getServiceLabelKey(serviceName);

  const handleToggle = (id: string) => {
    setOpenSectionId((current) => (current === id ? null : id));
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="analysis-preview-title"
      className="fixed inset-0 z-70 flex items-center justify-center bg-black/30 p-4"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="relative w-full max-w-[640px] rounded-2xl bg-day-light-5 shadow-xl outline-none max-h-[90vh] overflow-hidden"
      >
        <CloseButton
          onClick={onClose}
          ariaLabel={t('analysis-preview.close')}
          className="absolute right-4 top-4 z-10"
        />
        <div className="flex flex-col gap-5 p-7 overflow-y-auto max-h-[90vh]">
          <div className="flex items-center gap-2 text-xs text-day-neutral-subtle">
            <HiOutlineDocumentText size={16} aria-hidden />
            {serviceLabelKey && <span className="font-medium">{t(serviceLabelKey)}</span>}
            {serviceLabelKey && <span aria-hidden>·</span>}
            {isLoading ? (
              <span className="h-3 w-16 animate-pulse rounded bg-day-light-2" aria-hidden />
            ) : (
              <span>{t('analysis-preview.documents-count', { count: documentsCount })}</span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <h2
              id="analysis-preview-title"
              className="text-2xl font-semibold text-day-neutral-dark"
            >
              {t('analysis-preview.title')}
            </h2>
            <p className="text-sm text-day-neutral-subtle">{t('analysis-preview.subtitle')}</p>
          </div>

          <div className="flex flex-col gap-2">
            {sections.map((section) => (
              <SectionRow
                key={section.id}
                section={section}
                isOpen={openSectionId === section.id}
                onToggle={() => handleToggle(section.id)}
              />
            ))}
          </div>

          <div className="flex justify-end">
            <Button primary rounded onClick={onClose}>
              {t('analysis-preview.dismiss')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
